import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Alert,
  ActivityIndicator,
  FlatList,
  Modal,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useRentDiscountStore, DiscountRequestData } from '../../stores/rentDiscountStore';
import { RentDiscount, DiscountType, DiscountStatus } from '../../types';
import theme from '../../theme';

const isWeb = Platform.OS === 'web';

interface RentDiscountScreenProps {
  navigation?: any;
  // Optional: Pre-fill with driver/vehicle if coming from assignment
  route?: {
    params?: {
      driverId?: string;
      vehicleId?: string;
      driverName?: string;
      vehiclePlate?: string;
    };
  };
}

type ViewMode = 'request' | 'pending' | 'history';

export const RentDiscountScreen: React.FC<RentDiscountScreenProps> = ({ navigation, route }) => {
  // Store hooks
  const {
    discounts,
    pendingDiscounts,
    monthlyDiscountSummary,
    loading,
    error,
    requestDiscount,
    approveDiscount,
    rejectDiscount,
    getPendingDiscounts,
    getDiscountsByDriver,
    getMonthlyDiscountSummary,
    validateDiscountRequest,
    fetchDiscounts,
    clearError,
  } = useRentDiscountStore();

  // View mode
  const [viewMode, setViewMode] = useState<ViewMode>('request');

  // Request Form State
  const [driverId, setDriverId] = useState(route?.params?.driverId || '');
  const [driverName, setDriverName] = useState(route?.params?.driverName || '');
  const [vehicleId, setVehicleId] = useState(route?.params?.vehicleId || '');
  const [vehiclePlate, setVehiclePlate] = useState(route?.params?.vehiclePlate || '');
  const [discountType, setDiscountType] = useState<DiscountType>('service_day');
  const [discountDate, setDiscountDate] = useState('');
  const [discountDays, setDiscountDays] = useState('1');
  const [dailyRate, setDailyRate] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [validationMessage, setValidationMessage] = useState<{ valid: boolean; message: string } | null>(null);

  // History filters
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [statusFilter, setStatusFilter] = useState<'all' | DiscountStatus>('all');

  // Modals
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedDiscount, setSelectedDiscount] = useState<RentDiscount | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');

  // Load data based on view mode
  useEffect(() => {
    loadData();
  }, [viewMode, selectedMonth, selectedYear, statusFilter]);

  // Validate discount request when days change
  useEffect(() => {
    if (driverId && discountDays) {
      validateRequest();
    }
  }, [driverId, discountDays, selectedMonth, selectedYear]);

  const loadData = async () => {
    try {
      if (viewMode === 'pending') {
        await getPendingDiscounts();
      } else if (viewMode === 'history' && driverId) {
        await getDiscountsByDriver(driverId, selectedMonth, selectedYear);
        await getMonthlyDiscountSummary(driverId, selectedMonth, selectedYear);
      }
    } catch (err) {
      console.error('Error loading discount data:', err);
    }
  };

  const validateRequest = async () => {
    if (!driverId || !discountDays) {
      setValidationMessage(null);
      return;
    }

    try {
      const days = parseInt(discountDays);
      const result = await validateDiscountRequest(driverId, selectedMonth, selectedYear, days);
      setValidationMessage(result);
    } catch (err) {
      console.error('Validation error:', err);
    }
  };

  const handleRequestDiscount = async () => {
    // Validation
    if (!driverId || !driverName) {
      Alert.alert('Validation Error', 'Please select a driver');
      return;
    }
    if (!vehicleId || !vehiclePlate) {
      Alert.alert('Validation Error', 'Please select a vehicle');
      return;
    }
    if (!discountDate) {
      Alert.alert('Validation Error', 'Please enter discount date');
      return;
    }
    if (!discountDays || parseInt(discountDays) <= 0) {
      Alert.alert('Validation Error', 'Please enter valid number of days');
      return;
    }
    if (!dailyRate || parseFloat(dailyRate) <= 0) {
      Alert.alert('Validation Error', 'Please enter valid daily rate');
      return;
    }
    if (!reason.trim()) {
      Alert.alert('Validation Error', 'Please enter reason for discount');
      return;
    }

    const days = parseInt(discountDays);
    const rate = parseFloat(dailyRate);
    const totalAmount = days * rate;

    // Check validation
    if (validationMessage && !validationMessage.valid) {
      Alert.alert(
        'Exceeds Limit',
        `${validationMessage.message}\n\nTotal amount: AED ${totalAmount.toFixed(2)}`,
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'Request Anyway',
            style: 'destructive',
            onPress: submitRequest,
          },
        ]
      );
      return;
    }

    submitRequest();
  };

  const submitRequest = async () => {
    try {
      const requestData: DiscountRequestData = {
        driver_id: driverId,
        vehicle_id: vehicleId,
        discount_type: discountType,
        discount_date: discountDate,
        discount_days: parseInt(discountDays),
        daily_rate: parseFloat(dailyRate),
        reason: reason.trim(),
        notes: notes.trim() || undefined,
        requested_by: 'current-user-id', // TODO: Get from auth context
      };

      await requestDiscount(requestData);

      Alert.alert(
        'Success',
        `Discount request submitted successfully!\n\nDiscount: ${discountDays} days × AED ${dailyRate} = AED ${(parseInt(discountDays) * parseFloat(dailyRate)).toFixed(2)}`,
        [
          {
            text: 'OK',
            onPress: () => {
              // Reset form
              setDiscountDate('');
              setDiscountDays('1');
              setReason('');
              setNotes('');
              setValidationMessage(null);
              // Switch to history view
              setViewMode('history');
            },
          },
        ]
      );
    } catch (err: any) {
      Alert.alert('Error', err.message || 'Failed to submit discount request');
    }
  };

  const handleApprove = async (discount: RentDiscount) => {
    const message = validationMessage && !validationMessage.valid
      ? `${validationMessage.message}\n\nApprove anyway?`
      : `Approve ${discount.discount_days} day discount for ${discount.driver?.name}?`;

    Alert.alert('Approve Discount', message, [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Approve',
        style: 'default',
        onPress: async () => {
          try {
            const result = await approveDiscount(discount.id, 'current-user-id'); // TODO: Get from auth

            Alert.alert(
              result.message?.includes('Warning') ? 'Approved with Override' : 'Success',
              result.message || 'Discount approved successfully'
            );

            // Reload pending discounts
            await getPendingDiscounts();
          } catch (err: any) {
            Alert.alert('Error', err.message || 'Failed to approve discount');
          }
        },
      },
    ]);
  };

  const handleReject = async () => {
    if (!selectedDiscount) return;

    if (!rejectionReason.trim()) {
      Alert.alert('Validation Error', 'Please enter rejection reason');
      return;
    }

    try {
      await rejectDiscount(selectedDiscount.id, rejectionReason.trim(), 'current-user-id'); // TODO: Get from auth

      Alert.alert('Success', 'Discount request rejected');

      setShowRejectModal(false);
      setSelectedDiscount(null);
      setRejectionReason('');

      // Reload pending discounts
      await getPendingDiscounts();
    } catch (err: any) {
      Alert.alert('Error', err.message || 'Failed to reject discount');
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getDiscountTypeLabel = (type: DiscountType) => {
    const labels: Record<DiscountType, string> = {
      service_day: 'Service Day',
      breakdown: 'Breakdown',
      accident: 'Accident',
      manual: 'Manual',
      other: 'Other',
    };
    return labels[type];
  };

  const getStatusColor = (status: DiscountStatus) => {
    switch (status) {
      case 'pending':
        return theme.colors.warning;
      case 'approved':
        return theme.colors.success;
      case 'rejected':
        return theme.colors.error;
      default:
        return theme.colors.textSecondary;
    }
  };

  const renderRequestForm = () => (
    <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
      {/* Driver/Vehicle Selection (Simplified - assumes pre-filled) */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Driver & Vehicle</Text>
        <View style={styles.infoCard}>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Driver:</Text>
            <Text style={styles.infoValue}>{driverName || 'Not selected'}</Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Vehicle:</Text>
            <Text style={styles.infoValue}>{vehiclePlate || 'Not selected'}</Text>
          </View>
        </View>
      </View>

      {/* Discount Type */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Discount Type *</Text>
        <View style={styles.typeGrid}>
          {(['service_day', 'breakdown', 'accident', 'manual', 'other'] as DiscountType[]).map((type) => (
            <TouchableOpacity
              key={type}
              style={[styles.typeButton, discountType === type && styles.typeButtonActive]}
              onPress={() => setDiscountType(type)}
            >
              <Text style={[styles.typeText, discountType === type && styles.typeTextActive]}>
                {getDiscountTypeLabel(type)}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Discount Date */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Discount Date *</Text>
        <TextInput
          style={styles.input}
          value={discountDate}
          onChangeText={setDiscountDate}
          placeholder="YYYY-MM-DD (e.g., 2025-10-15)"
          placeholderTextColor={theme.colors.textSecondary}
        />
      </View>

      {/* Discount Days & Daily Rate */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Discount Details *</Text>
        <View style={styles.row}>
          <View style={styles.halfInput}>
            <Text style={styles.inputLabel}>Days</Text>
            <TextInput
              style={styles.input}
              value={discountDays}
              onChangeText={setDiscountDays}
              placeholder="1"
              keyboardType="numeric"
              placeholderTextColor={theme.colors.textSecondary}
            />
          </View>
          <View style={styles.halfInput}>
            <Text style={styles.inputLabel}>Daily Rate (AED)</Text>
            <TextInput
              style={styles.input}
              value={dailyRate}
              onChangeText={setDailyRate}
              placeholder="150.00"
              keyboardType="decimal-pad"
              placeholderTextColor={theme.colors.textSecondary}
            />
          </View>
        </View>

        {/* Calculated Total */}
        {discountDays && dailyRate && (
          <View style={styles.calculationBox}>
            <Text style={styles.calculationLabel}>Total Discount:</Text>
            <Text style={styles.calculationValue}>
              {discountDays} days × {formatCurrency(parseFloat(dailyRate))} = {formatCurrency(parseInt(discountDays) * parseFloat(dailyRate))}
            </Text>
          </View>
        )}
      </View>

      {/* Validation Message */}
      {validationMessage && (
        <View style={[styles.validationBox, validationMessage.valid ? styles.validationSuccess : styles.validationWarning]}>
          <Ionicons
            name={validationMessage.valid ? 'checkmark-circle' : 'warning'}
            size={20}
            color={validationMessage.valid ? theme.colors.success : '#E67E22'}
          />
          <Text style={[styles.validationText, { color: validationMessage.valid ? theme.colors.success : '#E67E22' }]}>
            {validationMessage.message}
          </Text>
        </View>
      )}

      {/* Reason */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Reason *</Text>
        <TextInput
          style={[styles.input, styles.textArea]}
          value={reason}
          onChangeText={setReason}
          placeholder="Enter reason for discount request..."
          multiline
          numberOfLines={3}
          textAlignVertical="top"
          placeholderTextColor={theme.colors.textSecondary}
        />
      </View>

      {/* Notes */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Notes (Optional)</Text>
        <TextInput
          style={[styles.input, styles.textArea]}
          value={notes}
          onChangeText={setNotes}
          placeholder="Additional notes..."
          multiline
          numberOfLines={3}
          textAlignVertical="top"
          placeholderTextColor={theme.colors.textSecondary}
        />
      </View>

      {/* Submit Button */}
      <TouchableOpacity style={styles.submitButton} onPress={handleRequestDiscount} disabled={loading}>
        {loading ? (
          <ActivityIndicator color="#FFFFFF" />
        ) : (
          <Text style={styles.submitButtonText}>Submit Discount Request</Text>
        )}
      </TouchableOpacity>
    </ScrollView>
  );

  const renderPendingApprovals = () => (
    <View style={styles.content}>
      {loading && pendingDiscounts.length === 0 ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.colors.primary} />
          <Text style={styles.loadingText}>Loading pending requests...</Text>
        </View>
      ) : pendingDiscounts.length === 0 ? (
        <View style={styles.emptyContainer}>
          <Ionicons name="checkmark-done-circle-outline" size={64} color={theme.colors.success} />
          <Text style={styles.emptyText}>No Pending Approvals</Text>
          <Text style={styles.emptySubtext}>All discount requests have been processed</Text>
        </View>
      ) : (
        <FlatList
          data={pendingDiscounts}
          keyExtractor={(item) => item.id}
          renderItem={({ item }) => (
            <View style={styles.discountCard}>
              {/* Header */}
              <View style={styles.discountHeader}>
                <View style={styles.discountTypeBox}>
                  <Text style={styles.discountTypeText}>{getDiscountTypeLabel(item.discount_type)}</Text>
                </View>
                <Text style={styles.discountAmount}>{formatCurrency(item.total_discount_amount)}</Text>
              </View>

              {/* Driver & Vehicle */}
              <View style={styles.discountInfo}>
                <View style={styles.discountRow}>
                  <Ionicons name="person-outline" size={16} color={theme.colors.textSecondary} />
                  <Text style={styles.discountText}>{item.driver?.name || 'Unknown Driver'}</Text>
                </View>
                <View style={styles.discountRow}>
                  <Ionicons name="car-outline" size={16} color={theme.colors.textSecondary} />
                  <Text style={styles.discountText}>{item.vehicle?.license_plate || 'Unknown Vehicle'}</Text>
                </View>
              </View>

              {/* Details */}
              <View style={styles.discountDetails}>
                <View style={styles.discountRow}>
                  <Ionicons name="calendar-outline" size={16} color={theme.colors.textSecondary} />
                  <Text style={styles.discountText}>
                    {formatDate(item.discount_date)} • {item.discount_days} {item.discount_days === 1 ? 'day' : 'days'}
                  </Text>
                </View>
                <View style={styles.discountRow}>
                  <Ionicons name="cash-outline" size={16} color={theme.colors.textSecondary} />
                  <Text style={styles.discountText}>{formatCurrency(item.daily_rate)}/day</Text>
                </View>
                <View style={styles.discountRow}>
                  <Ionicons name="information-circle-outline" size={16} color={theme.colors.textSecondary} />
                  <Text style={styles.discountText}>{item.reason}</Text>
                </View>
                {item.notes && (
                  <View style={styles.discountRow}>
                    <Ionicons name="document-text-outline" size={16} color={theme.colors.textSecondary} />
                    <Text style={styles.discountText}>{item.notes}</Text>
                  </View>
                )}
              </View>

              {/* Actions */}
              <View style={styles.discountActions}>
                <TouchableOpacity
                  style={[styles.actionButton, styles.approveButton]}
                  onPress={() => handleApprove(item)}
                >
                  <Ionicons name="checkmark-circle-outline" size={20} color="#FFFFFF" />
                  <Text style={styles.actionButtonText}>Approve</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.actionButton, styles.rejectButton]}
                  onPress={() => {
                    setSelectedDiscount(item);
                    setShowRejectModal(true);
                  }}
                >
                  <Ionicons name="close-circle-outline" size={20} color="#FFFFFF" />
                  <Text style={styles.actionButtonText}>Reject</Text>
                </TouchableOpacity>
              </View>
            </View>
          )}
          contentContainerStyle={styles.listContent}
        />
      )}
    </View>
  );

  const renderHistory = () => (
    <View style={styles.content}>
      {/* Month/Year Selector */}
      <View style={styles.filterBar}>
        <View style={styles.monthSelector}>
          <TouchableOpacity
            style={styles.selectorButton}
            onPress={() => {
              if (selectedMonth === 1) {
                setSelectedMonth(12);
                setSelectedYear(selectedYear - 1);
              } else {
                setSelectedMonth(selectedMonth - 1);
              }
            }}
          >
            <Ionicons name="chevron-back" size={20} color={theme.colors.textPrimary} />
          </TouchableOpacity>
          <Text style={styles.selectorText}>
            {new Date(selectedYear, selectedMonth - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </Text>
          <TouchableOpacity
            style={styles.selectorButton}
            onPress={() => {
              if (selectedMonth === 12) {
                setSelectedMonth(1);
                setSelectedYear(selectedYear + 1);
              } else {
                setSelectedMonth(selectedMonth + 1);
              }
            }}
          >
            <Ionicons name="chevron-forward" size={20} color={theme.colors.textPrimary} />
          </TouchableOpacity>
        </View>
      </View>

      {/* Status Filter */}
      <View style={styles.statusFilterBar}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          {(['all', 'pending', 'approved', 'rejected'] as const).map((status) => (
            <TouchableOpacity
              key={status}
              style={[styles.filterChip, statusFilter === status && styles.filterChipActive]}
              onPress={() => setStatusFilter(status)}
            >
              <Text style={[styles.filterChipText, statusFilter === status && styles.filterChipTextActive]}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {/* Monthly Summary */}
      {monthlyDiscountSummary && (
        <View style={styles.summaryCard}>
          <Text style={styles.summaryTitle}>Monthly Summary</Text>
          <View style={styles.summaryRow}>
            <View style={styles.summaryItem}>
              <Text style={styles.summaryValue}>{monthlyDiscountSummary.approved_days}</Text>
              <Text style={styles.summaryLabel}>Approved Days</Text>
            </View>
            <View style={styles.summaryItem}>
              <Text style={styles.summaryValue}>{monthlyDiscountSummary.pending_days}</Text>
              <Text style={styles.summaryLabel}>Pending Days</Text>
            </View>
            <View style={styles.summaryItem}>
              <Text style={styles.summaryValue}>{formatCurrency(monthlyDiscountSummary.total_discount_amount)}</Text>
              <Text style={styles.summaryLabel}>Total Discount</Text>
            </View>
          </View>
          {monthlyDiscountSummary.approved_days > 3 && (
            <View style={styles.warningBanner}>
              <Ionicons name="warning" size={16} color="#E67E22" />
              <Text style={styles.warningText}>Exceeds 3-day monthly limit (Manager override applied)</Text>
            </View>
          )}
        </View>
      )}

      {/* Discount List */}
      {loading && discounts.length === 0 ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.colors.primary} />
          <Text style={styles.loadingText}>Loading discount history...</Text>
        </View>
      ) : discounts.length === 0 ? (
        <View style={styles.emptyContainer}>
          <Ionicons name="pricetag-outline" size={64} color={theme.colors.textSecondary} />
          <Text style={styles.emptyText}>No Discounts Found</Text>
          <Text style={styles.emptySubtext}>No discount requests for this period</Text>
        </View>
      ) : (
        <FlatList
          data={discounts}
          keyExtractor={(item) => item.id}
          renderItem={({ item }) => (
            <View style={styles.historyCard}>
              {/* Header */}
              <View style={styles.historyHeader}>
                <View style={styles.discountTypeBox}>
                  <Text style={styles.discountTypeText}>{getDiscountTypeLabel(item.discount_type)}</Text>
                </View>
                <View style={[styles.statusBadge, { backgroundColor: getStatusColor(item.status) }]}>
                  <Text style={styles.statusText}>{item.status.toUpperCase()}</Text>
                </View>
              </View>

              {/* Details */}
              <View style={styles.historyDetails}>
                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>Date:</Text>
                  <Text style={styles.detailValue}>{formatDate(item.discount_date)}</Text>
                </View>
                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>Days:</Text>
                  <Text style={styles.detailValue}>{item.discount_days} days</Text>
                </View>
                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>Amount:</Text>
                  <Text style={styles.detailValue}>{formatCurrency(item.total_discount_amount)}</Text>
                </View>
                <View style={styles.detailRow}>
                  <Text style={styles.detailLabel}>Reason:</Text>
                  <Text style={styles.detailValue}>{item.reason}</Text>
                </View>
                {item.rejection_reason && (
                  <View style={styles.detailRow}>
                    <Text style={[styles.detailLabel, { color: theme.colors.error }]}>Rejection:</Text>
                    <Text style={[styles.detailValue, { color: theme.colors.error }]}>{item.rejection_reason}</Text>
                  </View>
                )}
              </View>
            </View>
          )}
          contentContainerStyle={styles.listContent}
        />
      )}
    </View>
  );

  const renderRejectModal = () => (
    <Modal visible={showRejectModal} transparent animationType="slide" onRequestClose={() => setShowRejectModal(false)}>
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Reject Discount Request</Text>
            <TouchableOpacity onPress={() => setShowRejectModal(false)}>
              <Ionicons name="close" size={24} color={theme.colors.textPrimary} />
            </TouchableOpacity>
          </View>

          <View style={styles.modalBody}>
            {selectedDiscount && (
              <>
                <Text style={styles.modalSubtitle}>
                  {selectedDiscount.driver?.name} - {selectedDiscount.discount_days} days
                </Text>
                <Text style={styles.modalAmount}>{formatCurrency(selectedDiscount.total_discount_amount)}</Text>

                <View style={styles.inputGroup}>
                  <Text style={styles.inputLabel}>Rejection Reason *</Text>
                  <TextInput
                    style={[styles.input, styles.textArea]}
                    value={rejectionReason}
                    onChangeText={setRejectionReason}
                    placeholder="Enter reason for rejection..."
                    multiline
                    numberOfLines={4}
                    textAlignVertical="top"
                    placeholderTextColor={theme.colors.textSecondary}
                  />
                </View>

                <TouchableOpacity style={[styles.submitButton, styles.rejectButtonFull]} onPress={handleReject}>
                  <Text style={styles.submitButtonText}>Reject Request</Text>
                </TouchableOpacity>
              </>
            )}
          </View>
        </View>
      </View>
    </Modal>
  );

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.headerTitle}>Company Share Adjustments</Text>
        <Text style={styles.headerSubtitle}>Request and manage company share adjustment approvals</Text>
      </View>

      {/* View Mode Tabs */}
      <View style={styles.tabBar}>
        <TouchableOpacity
          style={[styles.tab, viewMode === 'request' && styles.tabActive]}
          onPress={() => setViewMode('request')}
        >
          <Ionicons name="add-circle-outline" size={20} color={viewMode === 'request' ? theme.colors.primary : theme.colors.textSecondary} />
          <Text style={[styles.tabText, viewMode === 'request' && styles.tabTextActive]}>Request</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tab, viewMode === 'pending' && styles.tabActive]}
          onPress={() => setViewMode('pending')}
        >
          <Ionicons name="time-outline" size={20} color={viewMode === 'pending' ? theme.colors.primary : theme.colors.textSecondary} />
          <Text style={[styles.tabText, viewMode === 'pending' && styles.tabTextActive]}>
            Pending ({pendingDiscounts.length})
          </Text>
        </TouchableOpacity>
        <TouchableOpacity style={[styles.tab, viewMode === 'history' && styles.tabActive]} onPress={() => setViewMode('history')}>
          <Ionicons name="list-outline" size={20} color={viewMode === 'history' ? theme.colors.primary : theme.colors.textSecondary} />
          <Text style={[styles.tabText, viewMode === 'history' && styles.tabTextActive]}>History</Text>
        </TouchableOpacity>
      </View>

      {/* Content */}
      {viewMode === 'request' && renderRequestForm()}
      {viewMode === 'pending' && renderPendingApprovals()}
      {viewMode === 'history' && renderHistory()}

      {/* Reject Modal */}
      {renderRejectModal()}

      {/* Error Banner */}
      {error && (
        <View style={styles.errorBanner}>
          <Text style={styles.errorText}>{error}</Text>
          <TouchableOpacity onPress={clearError}>
            <Ionicons name="close" size={20} color="#FFFFFF" />
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  header: {
    padding: 16,
    backgroundColor: theme.colors.surface,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: theme.colors.textPrimary,
  },
  headerSubtitle: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    marginTop: 4,
  },
  tabBar: {
    flexDirection: 'row',
    backgroundColor: theme.colors.surface,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  tab: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    gap: 6,
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  tabActive: {
    borderBottomColor: theme.colors.primary,
  },
  tabText: {
    fontSize: 14,
    color: theme.colors.textSecondary,
  },
  tabTextActive: {
    color: theme.colors.primary,
    fontWeight: '600',
  },
  content: {
    flex: 1,
  },
  section: {
    padding: 16,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.textPrimary,
    marginBottom: 12,
  },
  infoCard: {
    backgroundColor: theme.colors.surface,
    borderRadius: 8,
    padding: 16,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 8,
  },
  infoLabel: {
    fontSize: 14,
    color: theme.colors.textSecondary,
  },
  infoValue: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.textPrimary,
  },
  typeGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  typeButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.border,
    backgroundColor: theme.colors.surface,
  },
  typeButtonActive: {
    backgroundColor: theme.colors.primary,
    borderColor: theme.colors.primary,
  },
  typeText: {
    fontSize: 14,
    color: theme.colors.textPrimary,
  },
  typeTextActive: {
    color: '#FFFFFF',
    fontWeight: '600',
  },
  input: {
    backgroundColor: theme.colors.surface,
    borderWidth: 1,
    borderColor: theme.colors.border,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16,
    color: theme.colors.textPrimary,
  },
  textArea: {
    minHeight: 80,
    paddingTop: 10,
  },
  row: {
    flexDirection: 'row',
    gap: 12,
  },
  halfInput: {
    flex: 1,
  },
  inputLabel: {
    fontSize: 12,
    color: theme.colors.textSecondary,
    marginBottom: 6,
  },
  calculationBox: {
    backgroundColor: theme.colors.success + '10',
    borderRadius: 8,
    padding: 12,
    marginTop: 12,
    borderWidth: 1,
    borderColor: theme.colors.success + '40',
  },
  calculationLabel: {
    fontSize: 12,
    color: theme.colors.textSecondary,
    marginBottom: 4,
  },
  calculationValue: {
    fontSize: 16,
    fontWeight: '700',
    color: theme.colors.success,
  },
  validationBox: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    marginHorizontal: 16,
    marginBottom: 16,
    borderRadius: 8,
    gap: 8,
  },
  validationSuccess: {
    backgroundColor: theme.colors.success + '10',
    borderWidth: 1,
    borderColor: theme.colors.success + '40',
  },
  validationWarning: {
    backgroundColor: '#FEF5E7',
    borderWidth: 1,
    borderColor: '#F39C12',
  },
  validationText: {
    flex: 1,
    fontSize: 14,
  },
  submitButton: {
    backgroundColor: theme.colors.primary,
    marginHorizontal: 16,
    marginBottom: 16,
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: 'center',
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: theme.colors.textSecondary,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  emptyText: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.colors.textPrimary,
    marginTop: 16,
  },
  emptySubtext: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    marginTop: 8,
    textAlign: 'center',
  },
  listContent: {
    padding: 16,
  },
  discountCard: {
    backgroundColor: theme.colors.surface,
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  discountHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  discountTypeBox: {
    backgroundColor: theme.colors.primary + '20',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
  },
  discountTypeText: {
    fontSize: 12,
    fontWeight: '600',
    color: theme.colors.primary,
  },
  discountAmount: {
    fontSize: 20,
    fontWeight: '700',
    color: theme.colors.textPrimary,
  },
  discountInfo: {
    marginBottom: 12,
    gap: 6,
  },
  discountDetails: {
    gap: 8,
  },
  discountRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  discountText: {
    flex: 1,
    fontSize: 14,
    color: theme.colors.textPrimary,
  },
  discountActions: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 12,
  },
  actionButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 10,
    borderRadius: 8,
    gap: 6,
  },
  approveButton: {
    backgroundColor: theme.colors.success,
  },
  rejectButton: {
    backgroundColor: theme.colors.error,
  },
  rejectButtonFull: {
    backgroundColor: theme.colors.error,
  },
  actionButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
  },
  filterBar: {
    padding: 16,
    backgroundColor: theme.colors.surface,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  monthSelector: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  selectorButton: {
    padding: 8,
  },
  selectorText: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.textPrimary,
  },
  statusFilterBar: {
    paddingVertical: 12,
    paddingLeft: 16,
    backgroundColor: theme.colors.surface,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  filterChip: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    marginRight: 8,
    borderRadius: 20,
    backgroundColor: theme.colors.background,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  filterChipActive: {
    backgroundColor: theme.colors.primary,
    borderColor: theme.colors.primary,
  },
  filterChipText: {
    fontSize: 14,
    color: theme.colors.textPrimary,
  },
  filterChipTextActive: {
    color: '#FFFFFF',
    fontWeight: '600',
  },
  summaryCard: {
    backgroundColor: theme.colors.surface,
    margin: 16,
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  summaryTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.textPrimary,
    marginBottom: 12,
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  summaryItem: {
    alignItems: 'center',
  },
  summaryValue: {
    fontSize: 20,
    fontWeight: '700',
    color: theme.colors.textPrimary,
    marginBottom: 4,
  },
  summaryLabel: {
    fontSize: 12,
    color: theme.colors.textSecondary,
  },
  warningBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FEF5E7',
    padding: 10,
    borderRadius: 6,
    marginTop: 12,
    gap: 8,
  },
  warningText: {
    flex: 1,
    fontSize: 12,
    color: '#E67E22',
  },
  historyCard: {
    backgroundColor: theme.colors.surface,
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  historyHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  statusBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
  },
  statusText: {
    fontSize: 12,
    fontWeight: '700',
    color: '#FFFFFF',
  },
  historyDetails: {
    gap: 8,
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  detailLabel: {
    fontSize: 14,
    color: theme.colors.textSecondary,
  },
  detailValue: {
    fontSize: 14,
    fontWeight: '500',
    color: theme.colors.textPrimary,
    flex: 1,
    textAlign: 'right',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: theme.colors.surface,
    borderRadius: 12,
    width: '90%',
    maxWidth: 500,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: theme.colors.textPrimary,
  },
  modalBody: {
    padding: 16,
  },
  modalSubtitle: {
    fontSize: 16,
    color: theme.colors.textPrimary,
    marginBottom: 8,
  },
  modalAmount: {
    fontSize: 24,
    fontWeight: '700',
    color: theme.colors.error,
    marginBottom: 24,
  },
  inputGroup: {
    marginBottom: 16,
  },
  errorBanner: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: theme.colors.error,
    padding: 16,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  errorText: {
    flex: 1,
    color: '#FFFFFF',
    fontSize: 14,
  },
});

export default RentDiscountScreen;
